import React, { useEffect, useRef, useState } from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { pickerValueToIsoDate } from '../../utils/pickerDate';

/**
 * [magánóra-sorozat, 2026-10-31, 3. web-validátor BLOKKOLÓ] Dátummező SAJÁT PISZKOZAT-értékkel — a létrehozó és a
 * szerkesztő mind a 4 dátummezője ezt használja (egy példány, nem négy másolat).
 *
 * MI VOLT A BAJ: a mezők vezérelt `value`-ja az ÁLLAPOTBÓL jött (`formData.eventDate` stb.). A 2. javító kör
 * (`utils/pickerDate.ts`) óta érvénytelen köztes értéket nem írtunk az állapotba — de ettől a `value` a RÉGI maradt,
 * és a MUI a mezőt a régi értékre igazította vissza. Mérve (böngésző): 31-i napról egyben gépelt `20270210` → a
 * köztes 2027-02-31 érvénytelen → a hónap visszaállt → a mezőben CSENDBEN 2027-10-10 lett (`20270415` → 2027-10-15).
 *
 * A MEGOLDÁS: a mező `value`-ja a helyi PISZKOZAT (az érvénytelen / félkész dayjs-t is megtartja, így a MUI a begépelt
 * szekciókat nem írja vissza). A szülő állapotába (`onChange`) CSAK érvényes, tartományon belüli nap kerül
 * (`pickerValueToIsoDate`). Ha a KÜLSŐ érték változik (megnyitás, visszaállítás, sablon-kitöltés) vagy a `syncKey`
 * (pl. a dialógus `open`-je), a piszkozat hozzá igazodik.
 *
 * A piszkozat ÁLLAPOTÁT (`valid` / `invalid` / `empty`) az `onDraftStatusChange` jelzi: a szülő ezzel TILTJA a mentést
 * („Érvénytelen dátum…" / „A dátum megadása kötelező"), hogy a félbehagyott gépelés ne a régi értékkel menjen ki csendben.
 */
export type DraftDateStatus = 'valid' | 'invalid' | 'empty';

export type DraftDatePickerProps = Omit<DatePickerProps, 'value' | 'onChange'> & {
  /** A szülő érvényes értéke (`YYYY-MM-DD`) vagy `''`. */
  value: string;
  /** Csak érvényes, tartományon belüli napra hív. */
  onChange: (isoDate: string) => void;
  onDraftStatusChange?: (status: DraftDateStatus) => void;
  /** Ennek változásakor a piszkozat a külső értékre áll vissza (pl. a dialógus megnyitása). */
  syncKey?: unknown;
};

const toDraft = (iso: string): Dayjs | null => (iso ? dayjs(iso) : null);

export function draftStatusOf(draft: Dayjs | null): DraftDateStatus {
  if (draft == null) return 'empty';
  return pickerValueToIsoDate(draft) ? 'valid' : 'invalid';
}

/** A piszkozat-állapotból a mentést tiltó üzenet (vagy `null`). `required`: az üres is hiba. */
export function draftDateError(status: DraftDateStatus | undefined, required: boolean): string | null {
  if (status === 'invalid') return 'Érvénytelen dátum — add meg teljesen (év, hónap, nap).';
  if (status === 'empty' && required) return 'A dátum megadása kötelező.';
  return null;
}

const DraftDatePicker: React.FC<DraftDatePickerProps> = ({ value, onChange, onDraftStatusChange, syncKey, ...rest }) => {
  const [draft, setDraft] = useState<Dayjs | null>(() => toDraft(value));
  const draftRef = useRef<Dayjs | null>(draft);
  const statusRef = useRef<DraftDateStatus | null>(null);
  /**
   * NEM VEZÉRELT mező (`defaultValue`), a külső változásra ÚJRA-MOUNTOLVA (`key`). MÉRT VERSENYHELYZETEK (böngésző,
   * gyors gépelés, 2026-10-31): (1) vezérelt `value` + csak-érvényes írás → a MUI a régi értékre igazította vissza a
   * hónapot (31-ről `20270210` → 2027-10-10); (2) vezérelt piszkozat + szinkron → a szülő késve visszaadott köztes
   * napja (`2027-04-01`) visszaírta a piszkozatot, és a MUI kiürítette a félkész szekciókat (`ÉÉÉÉ-HH-15`). Nem
   * vezérelt mezőnél gépelés közben a MUI-t SEMMI nem írja felül; a mező csak VALÓDI külső változásra (megnyitás =
   * `syncKey`, visszaállítás, betöltés — azaz nem a saját `onChange`-ünk visszhangja) indul újra a külső értékkel.
   */
  const emittedRef = useRef<string[]>([]);
  const [version, setVersion] = useState(0);

  const resetTo = (iso: string) => {
    emittedRef.current = [];
    const d = toDraft(iso);
    draftRef.current = d;
    setDraft(d);
    setVersion(v => v + 1);
  };

  useEffect(() => {
    resetTo(value);
    // Csak a szinkron-kulcsra (megnyitás / másik esemény): ilyenkor a külső érték a mérvadó.
  }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (value && emittedRef.current.includes(value)) return; // a saját visszhangunk
    if (draftRef.current != null && pickerValueToIsoDate(draftRef.current) === (value || null)) return;
    resetTo(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const status = draftStatusOf(draft);
    if (status !== statusRef.current) {
      statusRef.current = status;
      onDraftStatusChange?.(status);
    }
  }, [draft, onDraftStatusChange]);

  return (
    <DatePicker
      {...rest}
      key={version}
      defaultValue={toDraft(value)}
      onChange={(next) => {
        const d = (next as Dayjs | null) ?? null;
        draftRef.current = d;
        setDraft(d);
        const iso = pickerValueToIsoDate(d);
        if (iso && iso !== value) {
          emittedRef.current = [...emittedRef.current.slice(-9), iso];
          onChange(iso);
        }
      }}
    />
  );
};

export default DraftDatePicker;
